"use client";

import { faCommentPen } from "@fortawesome/pro-solid-svg-icons";
import { ErrorMessage } from "@hookform/error-message";
import { Input, Select, TextArea, formatNumber } from "@tosize/ui";
import { useFormContext } from "react-hook-form";
import type { ProblemCategoriesReturnType } from "~/app/api/types/ProblemCategories";
import type { OrderItemType } from "~/app/api/types/enum";
import type { HemlockContactFormType } from "../../Form";
import type { ContactProductTypes } from "../../types/schema";
import { ToggleField } from "./GeneralFieldsClient";

function LoadingInput() {
	return (
		<div className="flex flex-col gap-1">
			<div className="h-[24px] animate-pulse rounded bg-shades-light" />
			<div className="h-[42px] animate-pulse rounded bg-shades-light" />
		</div>
	);
}

interface ProductItemClientProps {
	index: number;
	defaultValues: { order_item_id: string; panel_id?: string; type: OrderItemType };
}

export function ProductItemClient({ index, defaultValues }: ProductItemClientProps) {
	const { register, resetField } = useFormContext<HemlockContactFormType>();

	return (
		<>
			<input
				type="hidden"
				{...register(`product.${index}.order_item_id`, { value: defaultValues.order_item_id })}
			/>
			<input
				type="hidden"
				{...register(`product.${index}.panel_id`, { value: defaultValues.panel_id })}
			/>

			<input
				type="hidden"
				{...register(`product.${index}.type`, {
					value: defaultValues.type as ContactProductTypes,
					onChange: () => {
						resetField(`product.${index}.issue.type`, { defaultValue: undefined });
						resetField(`product.${index}.issue.sub_type`, { defaultValue: undefined });
						resetField(`product.${index}.solution.type`, { defaultValue: undefined });
						resetField(`product.${index}.solution.amount`, { defaultValue: undefined });
						resetField(`product.${index}.solution.description`, { defaultValue: undefined });
					},
				})}
			/>
		</>
	);
}

interface IssuesFieldProps {
	inputs: ProblemCategoriesReturnType | null;
	index: number;
	label: string;
	chooseLabel: string;
	otherLabel: string;
}

export function IssuesField({ inputs, index, label, chooseLabel, otherLabel }: IssuesFieldProps) {
	const { watch, resetField } = useFormContext<HemlockContactFormType>();

	const subjectValue = watch("subject");
	const productTypeValue = watch(`product.${index}.type`);
	const issueTypeValue = watch(`product.${index}.issue.type`);

	if (!(inputs && subjectValue && productTypeValue)) {
		return <LoadingInput />;
	}

	if (
		subjectValue !== "order_product_complaint" &&
		subjectValue !== "order_product_not_delivered"
	) {
		return <LoadingInput />;
	}

	const currentInput = inputs[subjectValue][productTypeValue];
	const currentIssue = currentInput.find((category) => category.id === issueTypeValue);
	const isCategoryOther = currentIssue?.code === "other";

	return (
		<>
			<Select
				label={label}
				name={`product.${index}.issue.type`}
				registerOptions={{
					value: "",
					onChange: () => {
						resetField(`product.${index}.issue.sub_type`, { defaultValue: undefined });
						resetField(`product.${index}.solution.type`, { defaultValue: undefined });
						resetField(`product.${index}.solution.amount`, { defaultValue: undefined });
						resetField(`product.${index}.solution.description`, { defaultValue: undefined });
					},
				}}
			>
				<option value="">{chooseLabel}</option>

				{currentInput.map((category) => (
					<option key={category.id} value={category.id}>
						{category.name}
					</option>
				))}
			</Select>

			{isCategoryOther && (
				<Input placeholder={otherLabel} name={`product.${index}.issue.description`} />
			)}
		</>
	);
}

interface SubIssuesFieldProps {
	inputs: ProblemCategoriesReturnType | null;
	index: number;
	label: string;
	chooseLabel: string;
}

export function SubIssuesField({ inputs, index, label, chooseLabel }: SubIssuesFieldProps) {
	const { watch, resetField } = useFormContext<HemlockContactFormType>();
	const subjectValue = watch("subject");
	const productTypeValue = watch(`product.${index}.type`);
	const issueTypeValue = watch(`product.${index}.issue.type`);

	if (!(inputs && subjectValue && productTypeValue && issueTypeValue)) {
		return null;
	}

	if (
		subjectValue !== "order_product_complaint" &&
		subjectValue !== "order_product_not_delivered"
	) {
		return null;
	}

	const currentIssue = inputs[subjectValue][productTypeValue].find(
		(category) => category.id === issueTypeValue,
	);

	if (!currentIssue?.children?.length) {
		return null;
	}

	return (
		<Select
			label={label}
			name={`product.${index}.issue.sub_type`}
			registerOptions={{
				value: "",
				onChange: () => {
					resetField(`product.${index}.solution.type`);
					resetField(`product.${index}.solution.amount`);
					resetField(`product.${index}.solution.description`);
				},
			}}
		>
			<option disabled value="">
				{chooseLabel}
			</option>

			{currentIssue.children.map((child) => (
				<option key={child.id} value={child.id}>
					{child.name}
				</option>
			))}
		</Select>
	);
}

interface SolutionTypeFieldProps {
	inputs: ProblemCategoriesReturnType | null;
	index: number;
	label: string;
	chooseLabel: string;
	otherLabel: string;
}

export function SolutionTypeField(props: SolutionTypeFieldProps) {
	const { inputs, index, label, chooseLabel, otherLabel } = props;
	const { watch, resetField } = useFormContext<HemlockContactFormType>();
	const subjectValue = watch("subject");
	const productTypeValue = watch(`product.${index}.type`);
	const issueTypeValue = watch(`product.${index}.issue.type`);
	const issueSubTypeValue = watch(`product.${index}.issue.sub_type`);
	const solutionTypeValue = watch(`product.${index}.solution.type`);

	if (!(inputs && subjectValue && productTypeValue && issueTypeValue)) {
		return null;
	}

	if (
		subjectValue !== "order_product_complaint" &&
		subjectValue !== "order_product_not_delivered"
	) {
		return null;
	}

	const currentIssue = inputs[subjectValue][productTypeValue].find(
		(category) => category.id === issueTypeValue,
	);

	if (!!currentIssue?.children?.length && !issueSubTypeValue) {
		return null;
	}

	const currentSubIssue = currentIssue?.children?.find((child) => child.id === issueSubTypeValue);
	const solutionsForIssue = currentSubIssue?.solutions || currentIssue?.solutions;

	if (!solutionsForIssue?.length) {
		return null;
	}

	return (
		<>
			<Select
				label={label}
				name={`product.${index}.solution.type`}
				registerOptions={{
					value: "",
					onChange: () => {
						resetField(`product.${index}.solution.amount`);
						resetField(`product.${index}.solution.description`);
					},
				}}
			>
				<option disabled value="">
					{chooseLabel}
				</option>

				{solutionsForIssue.map((solution) => (
					<option key={solution.value} value={solution.value}>
						{solution.label}
					</option>
				))}
			</Select>

			{solutionTypeValue === "other" && (
				<Input placeholder={otherLabel} name={`product.${index}.solution.description`} />
			)}
		</>
	);
}

interface SolutionAmountFieldProps {
	lang: string;
	index: number;
	label: string;
	chooseLabel: string;
}

export function SolutionAmountField(props: SolutionAmountFieldProps) {
	const { lang, index, label, chooseLabel } = props;
	const { watch } = useFormContext<HemlockContactFormType>();
	const solutionTypeValue = watch(`product.${index}.solution.type`);
	const options = [25, 50, 75, 100] as const;

	if (solutionTypeValue !== "coupon" && solutionTypeValue !== "refund") {
		return null;
	}

	return (
		<Select
			label={label}
			name={`product.${index}.solution.amount`}
			registerOptions={{ value: "", valueAsNumber: true }}
		>
			<option disabled value="">
				{chooseLabel}
			</option>

			{options.map((option) => (
				<option key={option} value={option}>
					{formatNumber({
						lang,
						value: option / 100,
						style: "percent",
						digits: { min: 0, max: 0 },
					})}
				</option>
			))}
		</Select>
	);
}

interface SolutionDescriptionFieldProps {
	index: number;
	showLabel: string;
	label: string;
}

export function SolutionDescriptionField(props: SolutionDescriptionFieldProps) {
	const { index, showLabel, label } = props;
	const { watch } = useFormContext<HemlockContactFormType>();
	const solutionTypeValue = watch(`product.${index}.solution.type`);

	if (!solutionTypeValue || solutionTypeValue === "other") {
		return null;
	}

	return (
		<ToggleField variant="link" starticon={faCommentPen} showLabel={showLabel}>
			<TextArea label={label} name={`product.${index}.solution.description`} />
		</ToggleField>
	);
}

export function ContactOrderItemRoot() {
	const {
		watch,
		formState: { errors },
	} = useFormContext<HemlockContactFormType>();
	const products = watch("product");

	if ("product" in errors && products?.filter((p) => p.issue?.type).length === 0) {
		return (
			<div className="text-danger text-sm">
				<ErrorMessage name="product.root" />
			</div>
		);
	}

	return null;
}
